/**
 * Project tab data: pinned order (encrypted `project.tab`), badges from the same runs/inbox the
 * Now and Inbox counts use, the one-time bootstrap write, and reorder/pin/archive writes.
 * Without a stored order (older hub, write failed) the bootstrap order is used locally.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import { tabCounts } from '../../lib/lanes-model.js'
import { isLive, qk, useInbox, useProjects, useRuns } from '../../lib/queries.js'
import {
  bootstrapOrder,
  lastActivity,
  moveTab,
  needsBootstrap,
  pinnedTabs,
  pinTab,
  type TabWrite,
  unpinnedProjects,
  unpinTab,
} from '../../lib/tab-order.js'
import { useNeedsYou } from '../inbox/useNeedsYou.js'
import { errorText, useToast } from '../ui.js'
import type { TabItem } from './ProjectTabs.js'

/** Serialized across mounts: the bootstrap is written at most once per page load. */
let bootstrapped = false

export function useProjectTabs() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const projects = useProjects()
  const runs = useRuns()
  const inbox = useInbox()
  const needs = useNeedsYou()
  // Writes in flight: a refetch mid-sequence (each write broadcasts a change) must not show a
  // half-written order, so pending positions overlay whatever the server returned.
  const [pending, setPending] = useState<ReadonlyMap<string, number | null>>(new Map())
  const list = useMemo(
    () =>
      (projects.data ?? []).map((p) =>
        pending.has(p.id) ? { ...p, tab: { order: pending.get(p.id) ?? null } } : p,
      ),
    [projects.data, pending],
  )

  const fallback = useMemo(() => {
    if (!needsBootstrap(list)) return undefined
    return bootstrapOrder(list, lastActivity(runs.data ?? []))
  }, [list, runs.data])

  const write = useCallback(
    async (writes: TabWrite[]) => {
      if (!writes.length) return
      // Optimistic: the strip moves at once; the entity writes follow in order.
      setPending((prev) => new Map([...prev, ...writes.map((w) => [w.id, w.order] as const)]))
      try {
        for (const w of writes) await b.api.updateProject(w.id, { tab: { order: w.order } })
      } catch (e) {
        toast(`Could not save the tab order: ${errorText(e)}`, 'error')
      } finally {
        await qc.invalidateQueries({ queryKey: qk.projects })
        setPending((prev) => {
          const next = new Map(prev)
          for (const w of writes) if (next.get(w.id) === w.order) next.delete(w.id)
          return next
        })
      }
    },
    [b, qc, toast],
  )

  const once = useRef(false)
  useEffect(() => {
    if (!fallback || once.current || bootstrapped || !projects.isSuccess || !runs.isSuccess) return
    once.current = bootstrapped = true
    void write(fallback)
  }, [fallback, projects.isSuccess, runs.isSuccess, write])

  const pinned = useMemo(() => pinnedTabs(list, fallback), [list, fallback])
  const unpinned = useMemo(() => unpinnedProjects(list, pinned), [list, pinned])
  const now = Date.now()
  const counts = useMemo(
    () => tabCounts(list, runs.data ?? [], inbox.data ?? [], now),
    [list, runs.data, inbox.data, now],
  )
  const tabs: TabItem[] = pinned.map((p) => ({
    id: p.id,
    name: p.name,
    // One count source (lib/needs-you.ts): the same number the Inbox and Overview show.
    needs: needs.of(p.id),
    running: counts.get(p.id)?.running ?? 0,
  }))
  const totals = {
    needs: needs.total,
    running: (runs.data ?? []).filter(isLive).length,
  }
  const nav = useNavigate()
  return {
    projects,
    pinned,
    tabs,
    unpinned,
    totals,
    move: (id: string, to: number) => void write(moveTab(pinned, id, to)),
    unpin: (id: string) => void write(unpinTab(pinned, id)),
    pin: (id: string) => void write(pinTab(pinned, id)),
    archive: async (id: string) => {
      try {
        await b.api.updateProject(id, { status: 'archived', tab: { order: null } })
        await qc.invalidateQueries({ queryKey: qk.projects })
        const next = pinned.find((p) => p.id !== id)
        if (next) void nav({ to: '/p/$projectId', params: { projectId: next.id } })
        toast('Project archived')
      } catch (e) {
        toast(errorText(e), 'error')
      }
    },
  }
}
